import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { errorMessage } from "@/components/ui-bits";
import { client } from "@/utils/orpc";

import { seal } from "./seal";

type ActInput = Parameters<typeof client.servers.act>[0];
export type Action = ActInput["action"];

/** Call one agent action on a server (see packages/api/src/routers/servers.ts `act`). */
export async function act<T = Record<string, unknown>>(id: string, action: Action, params: Record<string, unknown> = {}) {
  return (await client.servers.act({ id, action, params })) as T;
}

/** Seal a secret to this server's agent before it leaves the browser. */
export async function sealFor(server: { id: string; agentPublicKey: string | null }, purpose: string, secret: string) {
  if (!server.agentPublicKey) throw new Error("This server hasn't reported its encryption key yet; update its agent first.");
  return seal(server.agentPublicKey, server.id, purpose, secret);
}

export function useAgentQuery<T>(id: string, action: Action, params: Record<string, unknown> = {}, enabled = true) {
  return useQuery({
    queryKey: ["act", id, action, params],
    queryFn: () => act<T>(id, action, params),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** A mutation that calls an agent action, toasts errors, and refreshes the given read actions. */
export function useAgentAction<V = Record<string, unknown>, T = Record<string, unknown>>(
  id: string,
  action: Action,
  toParams: (v: V) => Promise<Record<string, unknown>> | Record<string, unknown>,
  opts: { refresh?: Action[]; success?: string | ((r: T) => string) } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: V) => act<T>(id, action, await toParams(v)),
    onSuccess: (r) => {
      for (const a of opts.refresh ?? []) qc.invalidateQueries({ queryKey: ["act", id, a] });
      if (opts.success) toast.success(typeof opts.success === "function" ? opts.success(r) : opts.success);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
}
